import {Modal, Spin, Tree} from "antd";
import React, {useEffect, useRef, useState} from "react";
import { ObjTest, treeUtil } from "jzfz-web-utils";
import {request} from "../../util/request";
import {GLOBAL_LEARN_DATA_API} from "../../config/api";
import {File3Icon} from "../icon/Icon";
import CustomEmpty from "../customEmpty/CustomEmpty";
import { v4 as uuidv4 } from 'uuid';
import {ModuleEnum} from "../../config/constant";
import {FormInstance} from "antd/lib/form/hooks/useForm";


const { getListFromTree, getParentNodes } = treeUtil;


interface LearnDataModalProps {
    moduleType?: ModuleEnum;    // 模块类型
    form?: FormInstance;
    defaultCheckedKeys: string[];   // 默认选中key
    visible: boolean;
    onSubmit: (values: any[]) => void;
    onCancel: () => void;
}


/**
 * 学习资料选择弹窗组件
 * @param props
 * @constructor
 */
const LearnDataModal = (props: LearnDataModalProps) => {
    const { moduleType, form, defaultCheckedKeys, visible, onSubmit, onCancel } = props;
    // 目录树
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    // 线性目录树缓存
    const treeDataCache = useRef<any[]>([]);


    useEffect(() => {
        if (visible) {
           init();
        }
    }, [visible])


    /**
     * 初始化
     */
    const init = async () => {
        setCheckedKeys([]);
        setExpandedKeys([]);
        let data = await getTree();
        updateKeys(data, defaultCheckedKeys);
    }


    /**
     * 设置目录选中和展开节点
     * @param data
     * @param pkids
     */
    const updateKeys = (data: any[], pkids: string[]) => {
        if (!ObjTest.isNotEmptyArray(data) || !ObjTest.isNotEmptyArray(pkids)) {
            return;
        }
        let nodes = treeDataCache.current.filter((v: any) => pkids.includes(v.pkId));
        let keys = nodes.map((v: any) => v.key);
        let parentKeys = [];
        keys.forEach((v: any) => {
            let pkids = getParentNodes(v, data).map((v: any) => v.key);
            parentKeys = [...parentKeys, ...pkids];
        })
        setExpandedKeys(Array.from(new Set(parentKeys)));
        setCheckedKeys(keys);
    }


    /**
     * 给每个节点赋唯一key
     * @param data
     */
    const generateTree = (data: any[], parentKey: string): any[] => {
        return (data || []).map((v: any) => {
            let key = uuidv4();
            return {
                ...v,
                key,
                parentKey,
                children: ObjTest.isNotEmptyArray(v.children) ? generateTree(v.children, key) : [],
            }
        })
    }


    /**
     * 获取学习资料分类/包/内容树
     */
    const getTree = (): Promise<any> => {
        setLoading(true);
        return request('/category-menu/select/allContentTree', {}, {baseURL: GLOBAL_LEARN_DATA_API}).then((res: any) => {
            let data = generateTree(res.data || [], '0');
            setTreeData(loop(data));
            treeDataCache.current = getListFromTree(data);
            return data;
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 确认
     */
    const submit = () => {
        let target = treeDataCache.current.filter((v: any) => checkedKeys.includes(v.key));
        let pkids = target.map((v: any) => v.pkId);
        let values = Array.from(new Set(pkids));    // pkid去重

        setLoading(true);
        request('/api/learning-materials/query/connect', {materialPkIds: values}, {baseURL: GLOBAL_LEARN_DATA_API}).then((res: any) => {
            // 构建新的value值
            let newValue = res.data.map((v: any) => {
                return {pkid: v.pkid, name: v.name};
            });
            onSubmit(newValue);
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 勾选结点
     * @param info
     */
    const checkHandle = (info: any, e: any) => {
        const {checked, node} = e;
        // 获取真实pkid
        const { pkId } = node.dataRef;
        let target = treeDataCache.current.filter((v: any) => v.pkId === pkId);
        // 通过pkid寻找key(一个pkid可能对应多个不同key的节点)
        let keys = target.map((v: any) => v.key);
        if (checked) {
            setCheckedKeys((prevKeys: string[]) => [...prevKeys, ...keys]);
        }else {
            setCheckedKeys((prevKeys: string[]) => [...prevKeys.filter((v: any) => !keys.includes(v))]);
        }
    }


    /**
     * 展开树结点
     */
    const expandHandle = (expandedKeys: any[]) => {
        setExpandedKeys(expandedKeys);
    }


    /**
     * 获取checkbox禁用状态
     * @param data
     */
    const getDisabled = (data: any): boolean => {
        const {type, pkId} = data;
        // 不能添加该学习资料本身
        if (moduleType === ModuleEnum.learn && form) {
            const { materialId } = form.getFieldsValue();
            return type !== 3 || materialId === pkId;
        }
        return type !== 3;
    }


    const loop = (list: any[]): any[] => list.map((v: any) => {
        return {
            title: v.name,
            key: v.key,
            icon: v.type === 3 ? <File3Icon style={{color: '#1887F1'}} /> : null,
            disableCheckbox: getDisabled(v),
            dataRef: v,
            children: ObjTest.isNotEmptyArray(v.children) ? loop(v.children) : [],
        }
    })


    return (
        <Modal
            forceRender={true}
            maskClosable={false}
            width={600}
            title="关联学习资料"
            confirmLoading={loading}
            closable={false}
            visible={visible}
            onOk={submit}
            onCancel={onCancel}
            okButtonProps={{className: 'button-red'}}
        >
            <div style={{maxHeight: 600, overflow: "auto"}}>
                <Spin spinning={loading}>
                    {
                        ObjTest.isNotEmptyArray(treeData) ? (
                            <Tree
                                height={600}
                                checkable
                                selectable={false}
                                checkStrictly={true}
                                showIcon
                                checkedKeys={checkedKeys}
                                expandedKeys={expandedKeys}
                                treeData={treeData}
                                onCheck={checkHandle}
                                onExpand={expandHandle}
                            />
                        ) : <CustomEmpty loading={loading} />
                    }
                </Spin>
            </div>
        </Modal>
    )
}


export default LearnDataModal;
